import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { endpoints } from "@/lib/api";
import { useSettings } from "@/lib/useSettings";
import { getPricing } from "@/lib/pricing";
import Icon from "@/components/Icon";
import { Reveal } from "@/lib/motionSafe";
import ProductCard from "@/components/ProductCard";
import QuoteFormModal from "@/components/QuoteFormModal";
import { PRODUCT_IMAGE_FALLBACK } from "@/lib/imageFallback";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { settings } = useSettings();

  useEffect(() => {
    setProduct(null);
    setRelated([]);
    setNotFound(false);
    endpoints
      .product(slug)
      .then((p) => {
        setProduct(p);
        if (p.category_slug) {
          endpoints
            .products({ category: p.category_slug })
            .then((list) => setRelated(list.filter((x) => x.slug !== p.slug).slice(0, 4)))
            .catch(() => {});
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="product-not-found">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-[#0A1628]">المنتج غير موجود</h1>
          <Link to="/products" className="mt-6 inline-block aman-btn-light">تصفح المنتجات</Link>
        </div>
      </div>
    );
  }

  if (!product) return <div className="min-h-[60vh]" />;

  const p = getPricing(product);

  return (
    <div data-testid={`page-product-${product.slug}`}>
      <section className="pt-12 md:pt-16 pb-14 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* breadcrumb */}
          <nav className="text-sm text-slate-500 flex items-center gap-2 mb-6" aria-label="breadcrumb">
            <Link to="/products" className="hover:text-[#00b8cc] transition-colors">المنتجات</Link>
            <Icon name="chevron-left" size={14} />
            {product.category_slug && (
              <>
                <Link
                  to={`/products?category=${product.category_slug}`}
                  className="hover:text-[#00b8cc] transition-colors"
                  data-testid="breadcrumb-category"
                >
                  {product.category_title_ar}
                </Link>
                <Icon name="chevron-left" size={14} />
              </>
            )}
            <span className="text-[#0A1628] font-semibold truncate">{product.name_ar}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            {/* Image */}
            <Reveal>
              <div className="relative rounded-3xl overflow-hidden border border-slate-100 bg-slate-50">
                <img
                  src={product.image_url || PRODUCT_IMAGE_FALLBACK}
                  alt={product.name_ar}
                  className="w-full h-auto aspect-[4/3] object-cover"
                  onError={(e) => {
                    if (e.currentTarget.src !== PRODUCT_IMAGE_FALLBACK) {
                      e.currentTarget.src = PRODUCT_IMAGE_FALLBACK;
                    }
                  }}
                />
                {product.is_featured && !p.outOfStock && (
                  <span className="absolute top-4 start-4 text-xs font-bold text-[#0A1628] bg-[#00E5FF] px-3 py-1.5 rounded-full">
                    منتج مميّز
                  </span>
                )}
                {p.outOfStock && (
                  <span className="absolute top-4 start-4 text-xs font-bold text-white bg-slate-800 px-3 py-1.5 rounded-full shadow" data-testid="product-out-of-stock">
                    نفذت الكمية
                  </span>
                )}
                {p.onSale && !p.outOfStock && (
                  <span className="absolute top-4 end-4 text-xs font-bold text-white bg-rose-600 px-3 py-1.5 rounded-full shadow" data-testid="product-discount-badge">
                    {`خصم -${p.percent}%`}
                  </span>
                )}
              </div>
            </Reveal>

            {/* Info */}
            <Reveal delay={120}>
              <div>
                {product.category_title_ar && (
                  <span className="aman-tag-dark">{product.category_title_ar}</span>
                )}
                <h1 className="font-heading mt-4 text-3xl sm:text-4xl font-bold text-[#0A1628] leading-tight" data-testid="product-name">
                  {product.name_ar}
                </h1>
                <p className="mt-4 text-slate-600 text-lg leading-relaxed">{product.short_desc_ar}</p>

                <div className="mt-6 flex items-baseline gap-3 flex-wrap">
                  <span className="text-slate-500 text-sm">ابتداءً من</span>
                  {p.onSale ? (
                    <div className="flex items-baseline gap-3" dir="ltr" data-testid="product-price">
                      <span className="font-heading text-3xl font-bold text-rose-600">{p.saleStr}</span>
                      <span className="text-slate-400 text-lg line-through">{p.priceStr}</span>
                    </div>
                  ) : (
                    <span
                      className="font-heading text-3xl font-bold text-[#0A1628]"
                      dir="ltr"
                      data-testid="product-price"
                    >
                      {p.priceStr}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  السعر النهائي يعتمد على الكميات والتركيب. اطلب عرض سعر مخصص.
                </p>

                {p.outOfStock && (
                  <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-start gap-3" data-testid="product-out-of-stock-note">
                    <span className="mt-0.5 w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center shrink-0">
                      <Icon name="boxes" size={16} strokeWidth={2.2} />
                    </span>
                    <div className="text-sm text-slate-700 leading-relaxed">
                      <div className="font-bold text-[#0A1628]">نفذت الكمية</div>
                      تواصل معنا لمعرفة موعد التوفر التالي — سنُخبرك فور وصول الشحنة الجديدة.
                    </div>
                  </div>
                )}

                <div className="mt-7 flex flex-wrap gap-3">
                  <button
                    onClick={() => setQuoteOpen(true)}
                    className="aman-btn-light text-base"
                    data-testid="product-cta-quote"
                  >
                    <Icon name="send" size={18} strokeWidth={2} />
                    {p.outOfStock ? "استفسر عن التوفر" : "اطلب عرض سعر لهذا المنتج"}
                  </button>
                  {settings?.phone && (
                    <a
                      href={`tel:${settings.phone}`}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-6 py-3 text-[#0A1628] font-semibold hover:border-[#00E5FF] transition-colors"
                      data-testid="product-cta-call"
                    >
                      <Icon name="phone" size={18} />
                      اتصل بنا
                    </a>
                  )}
                </div>

                {product.long_desc_ar && (
                  <div className="mt-8 pt-6 border-t border-slate-100">
                    <h3 className="font-heading text-lg font-bold text-[#0A1628]">وصف المنتج</h3>
                    <p className="mt-3 text-slate-700 leading-loose">{product.long_desc_ar}</p>
                  </div>
                )}
              </div>
            </Reveal>
          </div>

          {/* Specs */}
          {product.specs?.length > 0 && (
            <Reveal>
              <div className="mt-14">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A1628]">المواصفات التقنية</h2>
                <div className="mt-6 rounded-2xl border border-slate-100 overflow-hidden" data-testid="product-specs-table">
                  <table className="w-full text-start">
                    <tbody>
                      {product.specs.map((s, i) => (
                        <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                          <th className="p-4 text-slate-600 font-medium text-sm w-1/3 text-start">{s.label_ar}</th>
                          <td className="p-4 text-[#0A1628] font-semibold text-sm">{s.value_ar}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* Related products */}
      {related.length > 0 && (
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <Reveal>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A1628]">منتجات مشابهة</h2>
              <p className="mt-2 text-slate-600">من نفس الفئة: {product.category_title_ar}</p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8" data-testid="related-products">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} delayMs={i * 60} />
              ))}
            </div>
          </div>
        </section>
      )}

      <QuoteFormModal
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        productId={product.id}
        productName={product.name_ar}
      />
    </div>
  );
}
